import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shuffle, Save, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MODES = [
  { key: "random", label: "Random", help: "Pick a random QR from all eligible QRs. This is the default." },
  { key: "branch_split", label: "Branch Percentage Split", help: "Pick a Branch by the percentages below, then a random eligible QR from that Branch. If that Branch has no eligible QR, the request goes to the other Branches." },
  { key: "least_used", label: "Least Used", help: "Pick the eligible QR with the lowest amount received today." },
];

export function QrRoutingPanel() {
  const [cfg, setCfg] = useState(null);
  const [branches, setBranches] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/super/qr-routing").then((r) => setCfg({ mode: r.data.mode, split: Object.fromEntries(Object.entries(r.data.branch_split || {}).map(([k, v]) => [k, String(v)])) })).catch((e) => toast.error(apiError(e)));
    api.get("/branches").then((r) => setBranches(r.data.filter((b) => b.role === "branch"))).catch(() => {});
  }, []);
  if (!cfg) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  const total = branches.reduce((s, b) => s + (parseFloat(cfg.split[b.id]) || 0), 0);
  const save = async () => {
    if (cfg.mode === "branch_split" && Math.abs(total - 100) > 0.01) return toast.error("Branch percentages must total 100");
    setBusy(true);
    try {
      const branch_split = cfg.mode === "branch_split" ? Object.fromEntries(branches.map((b) => [b.id, parseFloat(cfg.split[b.id]) || 0])) : {};
      await api.put("/super/qr-routing", { mode: cfg.mode, branch_split });
      toast.success("QR routing saved");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-4" data-testid="qr-routing-panel">
      <div>
        <div className="flex items-center gap-2"><Shuffle className="w-5 h-5 text-teal-600" /><h2 className="text-lg font-bold tracking-tight text-slate-900">QR Routing</h2></div>
        <p className="text-sm text-slate-500 mt-1">Choose how a QR is picked for the public page and the merchant API. Only Active QRs within the amount range, Branch limit and QR daily/lifetime limits are eligible. Changes apply immediately.</p>
      </div>
      <div className="space-y-2">
        {MODES.map((m) => (
          <label key={m.key} data-testid={`routing-mode-${m.key}`} className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${cfg.mode === m.key ? "border-teal-400 bg-teal-50/50" : "border-slate-200 hover:bg-slate-50"}`}>
            <input type="radio" name="routing" className="mt-1 accent-teal-600" checked={cfg.mode === m.key} onChange={() => setCfg({ ...cfg, mode: m.key })} />
            <div><div className="text-sm font-semibold text-slate-900">{m.label}</div><div className="text-xs text-slate-500">{m.help}</div></div>
          </label>
        ))}
      </div>
      {cfg.mode === "branch_split" && (
        <div className="rounded-lg border border-slate-200 p-3" data-testid="routing-split">
          {branches.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-3 py-1.5">
              <span className="text-sm text-slate-700">{b.name || b.email}</span>
              <div className="flex items-center gap-1"><Input data-testid={`routing-split-${b.id}`} type="number" min="0" step="0.01" value={cfg.split[b.id] ?? ""} onChange={(e) => setCfg({ ...cfg, split: { ...cfg.split, [b.id]: e.target.value } })} className="h-8 w-24 bg-white border-slate-300" /><span className="text-xs text-slate-500">%</span></div>
            </div>
          ))}
          <div className={`text-xs mt-2 font-semibold ${Math.abs(total - 100) > 0.01 ? "text-rose-600" : "text-emerald-600"}`} data-testid="routing-split-total">Total: {total}% (must equal 100%)</div>
        </div>
      )}
      <Button data-testid="routing-save" onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-700 text-white"><Save className="w-4 h-4 mr-1" />{busy ? "Saving..." : "Save"}</Button>
    </div>
  );
}
